/**
 * sliceBudget.js — сколько срезов класть в объём на этом устройстве.
 *
 * Отдельным файлом без импортов, потому что это правило обязано быть ОДНИМ на
 * все пути постройки объёма. В 10.0.4 появился второй путь — «объём по
 * требованию» (нажатие «Аксиальная»/«Объём» из стопки), и он этого правила не
 * знал: строил том из ПОЛНОЙ серии. Путь MPR при этом честно прореживал серию
 * до ~192 срезов. Один и тот же кабинетный случай — серия в 500 срезов на
 * телефоне — давал объём в два с половиной раза тяжелее в зависимости от того,
 * какой кнопкой его открыли. Снаружи это выглядело как «память была 80%,
 * стала 120%», а дальше браузер выгружал вкладку в фоне — и объём «переставал
 * держаться».
 *
 * Потолок 192 — не магия: это то число, при котором объём 512×512×N по два
 * байта на воксель остаётся в пределах ~100 МБ и телефонные браузеры не
 * начинают выгружать вкладку при сворачивании.
 */

export const MOBILE_SLICE_CAP = 192;

/**
 * @param {object} p
 * @param {number} p.count сколько срезов в серии
 * @param {boolean} [p.isMobile] телефонный экран
 * @param {number} [p.requestedStep] шаг, заданный явно (настройкой или протоколом) — уважается как есть
 * @param {number} [p.cap] потолок срезов для телефона
 * @returns {{step: number, usedCount: number, reason: string}}
 */
/**
 * [10.4.7] Потолок для слабой видеокарты. Живой случай: Lenovo B570,
 * Intel HD Graphics 3000, МРТ 332 среза 512×512. Объём — около 174 МБ
 * текстуры, и это на видеопамяти, которую карта делит с оперативной. В
 * журнале дальше по цепочке: не компилируется шейдер объёмного отрисовщика
 * (vtkVolumeVS.glsl), затем `setContext` на null, затем WebGL context lost —
 * семь раз подряд. КТ на 270 срезов та же машина рисует полностью.
 *
 * Потолок 300 выбран по двум замерам с этой же машины, а не наугад: 270
 * срезов КТ она рисует полностью — значит трогать их нельзя; 332 среза МРТ
 * роняют — значит их надо проредить. 300 проходит ровно между: КТ остаётся
 * нетронутой, МРТ идёт с шагом 2 (166 срезов, вдвое меньше текстуры).
 */
export const WEAK_GPU_SLICE_CAP = 300;

export function planSliceBudget({ count, isMobile = false, requestedStep = 1, cap = MOBILE_SLICE_CAP, isWeakGpu = false }) {
  const n = Math.max(0, Math.floor(count || 0));
  const req = Math.max(1, Math.floor(requestedStep || 1));

  if (!n) return { step: 1, usedCount: 0, reason: 'серия пуста' };

  // Явно заданный шаг важнее автоматики: человек уже решил, чем жертвует.
  if (req > 1) {
    return { step: req, usedCount: Math.ceil(n / req), reason: 'шаг задан явно' };
  }
  if (isMobile && n > cap) {
    const step = Math.ceil(n / cap);
    return { step, usedCount: Math.ceil(n / step), reason: `телефон: ${n} срезов не влезает, потолок ${cap}` };
  }
  if (isWeakGpu && n > WEAK_GPU_SLICE_CAP) {
    const step = Math.ceil(n / WEAK_GPU_SLICE_CAP);
    return { step, usedCount: Math.ceil(n / step), reason: `слабая видеокарта: ${n} срезов не влезает, потолок ${WEAK_GPU_SLICE_CAP}` };
  }
  return { step: 1, usedCount: n, reason: 'прореживание не требуется' };
}

/** Проредить список по шагу из planSliceBudget. Шаг 1 возвращает исходный список. */
export function thinByStep(imageIds, step) {
  if (!Array.isArray(imageIds)) return [];
  if (!step || step <= 1) return imageIds;
  return imageIds.filter((_, i) => i % step === 0);
}

export default { planSliceBudget, thinByStep, MOBILE_SLICE_CAP, WEAK_GPU_SLICE_CAP };
